import React, { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { ArrowLeft, Key, Upload } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { errorToast } from "../../shared/Toast";
import { getUserByID, updateUserById } from "../../utils/apiCalls";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, SectionTitle, Badge, Loader } from "../reporting/ReportingShared";
import { USER_TYPE_LABELS } from "../../constants/roles";
import PasswordModal from "./PasswordModal";
import userPlaceholder from "../../assets/images/placeholder.png";

// The signed-in user's id, from the stored JWT (the panel does not keep it elsewhere).
const signedInUserId = () => {
  try {
    const payload = localStorage.getItem("authToken")?.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")))?.id ?? null;
  } catch (_) {
    return null;
  }
};

const imageUrl = (path) => {
  if (!path) return userPlaceholder;
  return /^(https?:|blob:|data:)/.test(path) ? path : process.env.REACT_APP_URL + path;
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

const labelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: C.textLight,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 6,
  display: "block",
};
const inputStyle = {
  padding: "10px 12px",
  fontSize: 13,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontFamily: "inherit",
  outline: "none",
  color: C.text,
  background: C.white,
  width: "100%",
};
// min(100%, …) so the grid collapses to one column on a phone instead of overflowing.
const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
  gap: 16,
};
const primaryBtnStyle = {
  background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
  border: "none",
  borderRadius: 8,
  padding: "10px 24px",
  fontWeight: 600,
  color: C.white,
  boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
  cursor: "pointer",
  fontSize: 13,
};
const ghostBtnStyle = {
  background: C.white,
  border: `1.5px solid ${C.border}`,
  borderRadius: 8,
  padding: "10px 24px",
  fontWeight: 600,
  color: C.text,
  cursor: "pointer",
  fontSize: 13,
};

const InfoRow = ({ label, value }) => (
  <div>
    <div style={labelStyle}>{label}</div>
    <div style={{ fontSize: 13, color: C.text, fontWeight: 500, wordBreak: "break-word" }}>{value || "—"}</div>
  </div>
);

const EditUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem("userType") === "admin";
  const isSelf = String(signedInUserId()) === String(userId);

  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", phoneNo: "", email: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(userPlaceholder);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const applyUser = (data) => {
    setUser(data);
    setForm({
      firstName: data.firstName || "",
      lastName: data.lastName || "",
      phoneNo: data.phoneNo || "",
      email: data.email || "",
    });
    setImageFile(null);
    setImagePreview(imageUrl(data.profileImage));
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFetching(true);
      const response = await getUserByID(userId);
      if (cancelled) return;
      if (response?.data?.id) {
        applyUser(response.data);
        setFetching(false);
      } else {
        // The API answers 404 both for a missing id and for a user outside the caller's scope.
        errorToast("User not found");
        navigate("/users/list-user", { replace: true });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, navigate]);

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      errorToast("Use a JPG, PNG or WEBP image");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) {
      errorToast("First name is required");
      return;
    }
    const formData = new FormData();
    // First: multer picks the upload folder from dataType, and only sees fields sent before the file.
    formData.append("dataType", "user");
    formData.append("firstName", form.firstName);
    formData.append("lastName", form.lastName);
    formData.append("phoneNo", form.phoneNo);
    formData.append("email", form.email);
    if (imageFile) formData.append("image1", imageFile);

    setSaving(true);
    const updated = await updateUserById(userId, formData);
    setSaving(false);
    if (updated) navigate("/users/list-user");
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "User";
  const typeLabel = USER_TYPE_LABELS[String(user?.userType || "").toLowerCase()] || user?.userType || "—";
  const active = user?.active !== false;
  // Self → change (keeps this session). Admin on someone else → reset. Others: nothing.
  const passwordMode = isSelf ? "change" : isAdmin ? "reset" : null;

  return (
    <Fragment>
      <Breadcrumb title="Edit User" parent="Users" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {fetching ? (
            <Loader />
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Heading */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
                <div
                  onClick={() => navigate("/users/list-user")}
                  title="Back"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: `1.5px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    background: C.white,
                    flexShrink: 0,
                  }}
                >
                  <ArrowLeft size={16} color={C.textMed} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>Edit User</div>
                  <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>{fullName}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Badge color={C.blue} bg="rgba(102,126,234,0.12)">{typeLabel}</Badge>
                  <Badge color={active ? C.green : C.coral} bg={active ? "rgba(46,204,113,0.12)" : C.coralLight}>
                    {active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>

              <RCard>
                <SectionTitle>Profile</SectionTitle>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
                  <img
                    src={imagePreview}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.src = userPlaceholder;
                    }}
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: `1px solid ${C.border}`,
                      background: C.bg,
                    }}
                  />
                  <label style={{ ...ghostBtnStyle, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, margin: 0 }}>
                    <Upload size={14} />
                    {imageFile ? "Change photo" : "Upload photo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} style={{ display: "none" }} />
                  </label>
                </div>

                <div style={fieldGridStyle}>
                  <div>
                    <label style={labelStyle}>First Name *</label>
                    <input type="text" value={form.firstName} onChange={setField("firstName")} required style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Last Name</label>
                    <input type="text" value={form.lastName} onChange={setField("lastName")} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone</label>
                    <input type="tel" value={form.phoneNo} onChange={setField("phoneNo")} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input type="email" value={form.email} onChange={setField("email")} style={inputStyle} />
                  </div>
                </div>
              </RCard>

              <RCard>
                <SectionTitle>Account</SectionTitle>
                <div style={fieldGridStyle}>
                  <InfoRow label="User Type" value={typeLabel} />
                  <InfoRow label="Merchant" value={user?.Merchant?.name} />
                  <InfoRow label="Created" value={formatDate(user?.createdAt)} />
                  <InfoRow label="Last Login" value={formatDate(user?.lastLoginAt)} />
                </div>
              </RCard>

              {passwordMode && (
                <RCard>
                  <SectionTitle>Security</SectionTitle>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      padding: "12px 14px",
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Password</div>
                      <div style={{ fontSize: 11, color: C.textLight }}>
                        {passwordMode === "reset"
                          ? "Set a new password. The user is signed out everywhere."
                          : "Other devices will be signed out."}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPasswordOpen(true)}
                      style={{ ...ghostBtnStyle, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px" }}
                    >
                      <Key size={14} />
                      {passwordMode === "reset" ? "Reset password" : "Change password"}
                    </button>
                  </div>
                </RCard>
              )}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginBottom: 20 }}>
                <button type="button" onClick={() => navigate("/users/list-user")} style={ghostBtnStyle}>
                  Discard
                </button>
                <button type="submit" disabled={saving} style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          )}
        </div>
      </Container>

      {passwordMode && (
        <PasswordModal isOpen={passwordOpen} onClose={() => setPasswordOpen(false)} mode={passwordMode} user={user} />
      )}
    </Fragment>
  );
};

export default EditUser;
